<script setup lang="ts">
import SettingFormInput from "@/components/VehicleFormParts/SettingFormInput.vue";
import Toggle from "@/components/VehicleFormParts/SettingFormToggle.vue";
import {defineProps, ref, toRefs, watch, reactive, computed} from 'vue'
import i18n from '@/lang/i18n';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  selectDefault: Boolean,
  errors: { type: Object, default: () => ({}) },
  formInputs: {},
  operationOfficeId: null,
  deviceId: null,
  isInstallationOffice: {
    type: Boolean,
    default: false
  },
})
const isSpeedLimitAlert = ref('ON');
const setDelayTime = ref<number>(0);
const isAlertTiming = ref('1');
const setSpeedLimitInterval = ref<string>('15');
const selectItemSpeedLimitInterval = reactive([
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.continuous'), value: '0' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.fifteenSeconds'), value: '15' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.thirtySeconds'), value: '30' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.fortyFiveSeconds'), value: '45' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.sixtySeconds'), value: '60' },
]);
const selectItemSpeedLimitTiming = reactive([
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.onlyExcess'), value: '0' },
  { title: i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.continuation'), value: '1' },
]);
const readonlyInputs = ref<any[]>([]);
const { formInputs } = toRefs(props);
const errorKeyPrefix = ref('initial_params.speed_alert');

const form = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
})

const formFieldOptions = computed(() => {
  const parentKey = 'speed_alert';

  return {
    isSpeedLimitAlert: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_on', 'speed_limit_normal_on']),
    isAlertTiming: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_timing', 'speed_limit_normal_timing']),
    speedLimitHigh: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high']),
    speedLimitNormal: getShowAndRequiredAndErrors(parentKey, ['speed_limit_normal']),
    setDelayTime: getShowAndRequiredAndErrors(parentKey, ['speed_limit_high_delay', 'speed_limit_normal_delay']),
  };
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, string | boolean | number>): void;
}>();

const autoCompleteToggles = () => {
  isSpeedLimitAlert.value = form.value.speed_limit_high_on == 'ON' && form.value.speed_limit_normal_on == 'ON' ? 'ON' : 'OFF';
  isAlertTiming.value = form.value.speed_limit_normal_timing == '1' && form.value.speed_limit_high_timing == '1' ? '1' : '0';
};

const autoCompleteDelays = () => {
  if (form.value.speed_limit_normal_delay && form.value.speed_limit_high_delay) {
    setDelayTime.value = form.value.speed_limit_normal_delay;
  }
};

const autoCompleteSelects = () => {
  if (form.value.speed_limit_normal_interval && form.value.speed_limit_high_interval) {
    setSpeedLimitInterval.value = form.value.speed_limit_normal_interval;
  }
};

const getShowAndRequiredAndErrors = (parentKey: string, formKeys: string[]) => {
  if (!props.formInputs?.form[parentKey]) {
    return {
      isShow: false,
      isRequired: false,
      errors: null
    };
  }

  const keys = Object.keys(props.formInputs.form[parentKey]);

  const result = formKeys.map(formKey => {
    return {
      isShow: keys.includes(formKey),
      isRequired: !!props.formInputs.form[parentKey][formKey]?.required,
      errors: props.errors[`${errorKeyPrefix.value}.${formKey}`]
    };
  });

  return {
    isShow: result.every(item => item.isShow),
    isRequired: result.every(item => item.isRequired),
    errors: result.map(item => item.errors).filter(error => error)
  };
};

watch(() => isSpeedLimitAlert.value, (value) => {
  form.value.speed_limit_high_on = value;
  form.value.speed_limit_normal_on = value;
});

watch(() => setDelayTime.value, (value) => {
  form.value.speed_limit_high_delay = value;
  form.value.speed_limit_normal_delay = value;
});

watch(() => isAlertTiming.value, (value) => {
  form.value.speed_limit_high_timing = value;
  form.value.speed_limit_normal_timing = value;
});

watch(() => setSpeedLimitInterval.value, (value) => {
  form.value.speed_limit_high_interval = value;
  form.value.speed_limit_normal_interval = value;
});

watch(() => form.value.speed_limit_high_on, (value) => {
  form.value.speed_limit_normal_on = value;
});

watch(() => props.modelValue, () => {
  if (props.modelValue) {
    autoCompleteToggles();
    autoCompleteDelays();
    autoCompleteSelects();
  }
}, { immediate: true });
</script>

<template>
  <div class="mr-3 v-theme--light">
    <Toggle
        :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.speedLimitAlert')"
        v-model="isSpeedLimitAlert"
        :formInputs="formInputs"
        :isShow="formFieldOptions.isSpeedLimitAlert.isShow"
        :isRequired="formFieldOptions.isSpeedLimitAlert.isRequired"
        :disabled="(selectDefault && readonlyInputs.includes('speed_limit_high_on') && readonlyInputs.includes('speed_limit_normal_on')) || isInstallationOffice"
    />
  </div>
  <div class="row mr-3 v-theme--light ml-4 no-padding" v-if="isSpeedLimitAlert === 'ON'">
    <div class="row align-items-center no-padding">
      <div class="col-12 col-lg">
        <label class="form-label">
          {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertSpeedSettings') }}
        </label>
      </div>
      <div class="col">
        <SettingFormInput
            :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.expresswayKph')"
            v-model="form.speed_limit_high"
            :isShow="formFieldOptions.speedLimitHigh.isShow"
            :isRequired="formFieldOptions.speedLimitHigh.isRequired"
            type="number"
            :max="200"
            :colClassNumber="0"
            :formInputs="formInputs"
            :readonly="(selectDefault && readonlyInputs.includes('speed_limit_high')) || isInstallationOffice"
            :errors="formFieldOptions.speedLimitHigh.errors"
        />
        <SettingFormInput
            :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.generalRoadKph')"
            v-model="form.speed_limit_normal"
            :isShow="formFieldOptions.speedLimitNormal.isShow"
            :isRequired="formFieldOptions.speedLimitNormal.isRequired"
            type="number"
            :max="200"
            :colClassNumber="0"
            :formInputs="formInputs"
            :readonly="(selectDefault && readonlyInputs.includes('speed_limit_normal')) || isInstallationOffice"
            :errors="formFieldOptions.speedLimitNormal.errors"
        />
      </div>
    </div>
    <div class="row align-items-center no-padding">
      <div class="col-12 col-lg">
        <label class="form-label">
          {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.decisionTime') }}
        </label>
      </div>
      <div class="col">
        <SettingFormInput
            :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.decisionTimeSeconds')"
            v-model="setDelayTime"
            :isShow="formFieldOptions.setDelayTime.isShow"
            :isRequired="formFieldOptions.setDelayTime.isRequired"
            type="number"
            :max="600"
            :colClassNumber="0"
            :formInputs="formInputs"
            :readonly="(selectDefault && readonlyInputs.includes('speed_limit_high_delay') && readonlyInputs.includes('speed_limit_normal_delay')) || isInstallationOffice"
            :errors="formFieldOptions.setDelayTime.errors"
        />
      </div>
    </div>

    <div class="row align-items-center no-padding">
      <div class="col-12 col-lg">
        <label class="form-label">
          {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertTiming') }}
        </label>
      </div>
      <div class="col">
        <v-select
            hide-details="auto"
            class="small mt-2"
            :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertTimingLabel')"
            v-model="isAlertTiming"
            :items="selectItemSpeedLimitTiming"
            :clearable="false"
            :disabled="(selectDefault && readonlyInputs.includes('speed_limit_high_timing') && readonlyInputs.includes('speed_limit_normal_timing')) || isInstallationOffice"
        >
        </v-select>
      </div>
    </div>
    <div class="row align-items-center no-padding" v-if="isAlertTiming === '1'">
      <div class="col">
        <label class="form-label">
          {{ i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertInterval') }}
        </label>
      </div>
      <div class="col">
        <v-select
            hide-details="auto"
            class="small mt-2"
            :label="i18n.global.t('pages.vehicles.form.iniFileSetting.speedAlert.alertInterval')"
            v-model="setSpeedLimitInterval"
            :items="selectItemSpeedLimitInterval"
            :clearable="false"
            :disabled="isInstallationOffice"
        >
        </v-select>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-padding {
  padding: 0 !important;
}
.headline {
  font-size: 24px;
  font-weight: bold;
  color: #0088a6;
}
</style>
